import type { NavStallDetail } from '@libs/templates/routing';
import { compressStack, type PendoField, shortenUrl } from './pendoSanitizer';

/** 送信時点の画面の状態。全 event の先頭に付ける */
interface PageContext {
  origin: string;
  path: string;
  version?: string;
  /** ページを読み込んでからの ms */
  uptime: number;
}

interface JsErrorLike {
  message: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  error?: unknown;
}

interface ResourceTargetLike {
  tagName: string;
  src: string;
}

const getPageContext = (): PageContext => ({
  origin: window.location.origin,
  path: window.location.pathname,
  version: process.env.APP_VERSION,
  uptime: Math.round(performance.now()),
});

const commonFields = (ctx: PageContext): PendoField[] => [
  ['path', ctx.path],
  ['v', ctx.version],
  ['up', ctx.uptime],
];

const toStackField = (stack: unknown, ctx: PageContext): string | undefined => {
  return typeof stack === 'string' ? compressStack(stack, ctx.origin) : undefined;
};

const toSrcField = (src: unknown, ctx: PageContext): string | undefined => {
  return typeof src === 'string' && src ? shortenUrl(src, ctx.origin) : undefined;
};

/** ErrorBoundary が送る `extractErrorInfo` の結果から作る */
const buildReactErrorFields = (info: Record<string, unknown>, ctx: PageContext): PendoField[] => [
  ...commonFields(ctx),
  ['name', info.name ?? info.type],
  ['kind', info.chunkType],
  ['src', toSrcField(info.request, ctx)],
  ['status', info.status],
  ['message', info.message ?? info.raw],
  ['stack', toStackField(info.stack, ctx)],
  ['data', info.data],
];

const buildJsErrorFields = (event: JsErrorLike, ctx: PageContext): PendoField[] => {
  const error = event.error instanceof Error ? event.error : undefined;
  const stack = toStackField(error?.stack, ctx);
  const location = event.filename
    ? `${shortenUrl(event.filename, ctx.origin)}:${event.lineno}:${event.colno}`
    : undefined;
  return [
    ...commonFields(ctx),
    ['name', error?.name],
    ['message', event.message],
    // stack が取れないときだけ、発生位置を残す
    ['stack', stack || location],
  ];
};

const buildRejectionFields = (reason: unknown, ctx: PageContext): PendoField[] => {
  if (reason instanceof Error) {
    return [
      ...commonFields(ctx),
      ['name', reason.name],
      ['message', reason.message],
      ['stack', toStackField(reason.stack, ctx)],
    ];
  }
  return [...commonFields(ctx), ['reason', reason]];
};

const buildResourceErrorFields = (target: ResourceTargetLike, ctx: PageContext): PendoField[] => [
  ...commonFields(ctx),
  ['tagName', target.tagName],
  ['src', toSrcField(target.src, ctx)],
];

/** 画面遷移が止まったときのブラウザの状態 */
interface NavStallEnv {
  /** 取得中の chunk（runtime が挿入し、完了すると取り除く `script[data-rspack]`）の URL */
  pendingScripts: string[];
  /** 待っている間に main thread が止まっていた合計の ms */
  longTaskMs: number;
  visibility: DocumentVisibilityState;
  isOnline: boolean;
  rtt?: number;
}

const collectNavStallEnv = (longTaskMs: number): NavStallEnv => {
  const connection =
    'connection' in navigator ? (navigator.connection as { rtt?: number } | undefined) : undefined;
  return {
    pendingScripts: Array.from(
      document.querySelectorAll<HTMLScriptElement>('script[data-rspack]'),
      (s) => s.src,
    ),
    longTaskMs,
    visibility: document.visibilityState,
    isOnline: navigator.onLine,
    rtt: connection?.rtt,
  };
};

/**
 * `pending` があれば chunk の取得が終わっていない（H1）、
 * `longTask` が大きければ main thread が止まっていた（H2）と判定できる。
 * 送る時点で遷移は確定しているので、遷移先は共通の `path` で分かる。
 */
const buildNavStallFields = (detail: NavStallDetail, env: NavStallEnv, ctx: PageContext): PendoField[] => [
  ...commonFields(ctx),
  ['from', detail.fromPathname],
  ['waited', detail.waitedMs],
  ['pendingN', env.pendingScripts.length],
  ['longTask', env.longTaskMs],
  ['vis', env.visibility],
  ['online', env.isOnline],
  ['rtt', env.rtt],
  ['pending', env.pendingScripts.map((src) => shortenUrl(src, ctx.origin)).join(' ')],
];

const buildLongTaskFields = (duration: number, after: string | undefined, ctx: PageContext): PendoField[] => [
  ...commonFields(ctx),
  ['dur', Math.round(duration)],
  ['after', after],
];

export type { JsErrorLike, NavStallEnv, PageContext, ResourceTargetLike };
export {
  buildJsErrorFields,
  buildLongTaskFields,
  buildNavStallFields,
  buildReactErrorFields,
  buildRejectionFields,
  buildResourceErrorFields,
  collectNavStallEnv,
  commonFields,
  getPageContext,
};
